import React, { useState, useEffect } from 'react';
import ModalHeader from '../common/ModalHeader';
import FormField from '../common/FormField';
import CommentSection from '../common/CommentSection';
import ActionButtons from '../common/ActionButtons';
import { useToastService } from '../toast/ToastService';
import './AddTaskModal.scss';
import {
  fetchWells,
  fetchUsers,
  fetchUserGroups,
  fetchWellGroups,
  saveTaskData,
  fetchCategories,
} from '../../services/dashboard/TaskDetailsService';
import { TaskSaveData, UserData, UserGroupData, WellData, WellGroupData } from './AddTaskDataModals';
import { CategoryData, CategoryDataTier1, TaskData } from '../task-details-widget/TaskDataModels';
import { useAppSelector } from '../../hooks/storeHooks';
import { createPortal } from 'react-dom';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskToEdit?: TaskData | null;
  onTaskSaved?: () => void;
}

interface FormData {
  well: string;
  assignedTo: string;
  userGroup: string;
  wellGroup: string;
  category: string;
  dueDate: Date | null;
  task: string;
  taskComment: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

interface TouchedFields {
  [key: string]: boolean;
}

const initialFormData: FormData = {
  well: '',
  assignedTo: '',
  userGroup: '',
  wellGroup: '',
  category: '',
  dueDate: null,
  task: '',
  taskComment: '',
};

const AddTaskModal: React.FC<AddTaskModalProps> = ({ isOpen, onClose, taskToEdit = null, onTaskSaved }) => {
  const [formData, setFormData] = useState<FormData>({ ...initialFormData });
  const [errors, setErrors] = useState<FormErrors>({});
  const [, setTouched] = useState<TouchedFields>({});
  const [assignmentType, setAssignmentType] = useState<string>('');
  const [wells, setWells] = useState<WellData[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroupData[]>([]);
  const [wellGroups, setWellGroups] = useState<WellGroupData[]>([]);
  const [categories, setCategories] = useState<CategoryData[] | CategoryDataTier1[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const wellTestId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);
  const { showSuccessToast, showErrorToast } = useToastService();

  useEffect(() => {
    const initializeFormData = () => {
      // Reset form data and errors when modal is closed
      if (!isOpen) {
        setFormData({ ...initialFormData });
        setErrors({});
        setTouched({});
        setAssignmentType('');
        return;
      }

      // Set default values (7 days later for due date)
      if (!taskToEdit) {
        const today = new Date();
        const sevenDaysLater = new Date(today);
        sevenDaysLater.setDate(today.getDate() + 7);
        setFormData({
          ...initialFormData,
          well: wellTestId?.toString() || '',
          dueDate: sevenDaysLater,
        });
      }

      if (taskToEdit) {
        const assignType = taskToEdit.UserGroupId ? 'userGroup' : taskToEdit.WellGroupId ? 'wellGroup' : '';
        setFormData({
          well: taskToEdit.WellId?.toString() || '',
          assignedTo: taskToEdit.UserId?.toString() || '',
          userGroup: taskToEdit.UserGroupId?.toString() || '',
          wellGroup: taskToEdit.WellGroupId?.toString() || '',
          category: taskToEdit.CategoryId?.toString() || '',
          dueDate: taskToEdit.DueDate ? new Date(taskToEdit.DueDate) : null,
          task: taskToEdit.Task || '',
          taskComment: taskToEdit.Comment || '',
        });
        setAssignmentType(assignType);
      }
    };

    const initializeDropdowns = async () => {
      if (!isOpen) return;

      try {
        const [wellsData, usersData, userGroupsData, wellGroupsData, categoriesData] = await Promise.all([
          fetchWells(),
          fetchUsers(),
          fetchUserGroups(),
          fetchWellGroups(),
          fetchCategories(),
        ]);

        setWells(wellsData);
        setUsers(usersData);
        setUserGroups(userGroupsData);
        setWellGroups(wellGroupsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };

    initializeFormData();
    initializeDropdowns();
  }, [isOpen, taskToEdit]);

  const handleInputChange = (name: string, value: string | Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error || undefined,
    }));
  };

  const validateField = (name: string, value: string | Date | null): string => {
    if (['well'].includes(name) && !value) return 'The well field is required';
    if (['assignedTo'].includes(name) && !value) return 'The Assigned to is required';
    if (name === 'dueDate' && !value) return 'Due Date is required';
    if (name === 'task' && !value) return 'Task is required';
    return '';
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    let valid = true;

    ['well', 'assignedTo', 'dueDate', 'task'].forEach((field) => {
      const error = validateField(field, formData[field as keyof FormData]);
      if (error) {
        newErrors[field] = error;
        valid = false;
      }
    });

    const hasUserGroup = !!formData.userGroup;
    const hasWellGroup = !!formData.wellGroup;
    if (!hasUserGroup && !hasWellGroup) {
      newErrors.userGroup = 'Either User Group or Well Group must be selected';
      newErrors.wellGroup = 'Either User Group or Well Group must be selected';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleAssignmentTypeChange = (type: string, name: string, value: string | Date | null) => {
    setAssignmentType(type);

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Clear the opposite group in the same update
      ...(type === 'userGroup' ? { wellGroup: '' } : {}),
      ...(type === 'wellGroup' ? { userGroup: '' } : {}),
    }));

    // Also update error and touched state
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error || undefined,
      ...(type === 'userGroup' ? { wellGroup: undefined } : {}),
      ...(type === 'wellGroup' ? { userGroup: undefined } : {}),
    }));
  };

  const prepareTaskData = (): TaskSaveData => {
    const getValue = <T extends { [key: string]: any }>(list: T[], key: string, field: string) =>
      list.find((item) => item[field].toString() === key);

    const well = getValue(wells, formData.well, 'Key');
    const user = getValue(users, formData.assignedTo, 'UserId');
    const category = getValue(categories as (CategoryData | CategoryDataTier1)[], formData.category, 'CategoryId');
    const userGroup = getValue(userGroups, formData.userGroup, 'UserGroupId');
    const wellGroup = getValue(wellGroups, formData.wellGroup, 'WellGroupId');

    return {
      ...(taskToEdit?.WellTaskId ? { WellTaskId: taskToEdit.WellTaskId } : {}),
      WellId: parseInt(formData.well),
      UserId: parseInt(formData.assignedTo),
      Task: formData.task,
      Comment: formData.taskComment,
      DueDate: formData.dueDate ? formData.dueDate.toISOString().split('T')[0] : '',
      CategoryId: formData.category ? parseInt(formData.category) : null,
      CategoryName: (category && 'Name' in category) ? category.Name : '',
      WellName: well?.Value || '',
      AssignedTo: user?.Name || '',
      UserName: user?.Name || '',
      UserAlias: user?.Alias || '',
      ...(assignmentType === 'userGroup'
        ? { UserGroupId: parseInt(formData.userGroup), UserGroupName: userGroup?.Name || '' }
        : assignmentType === 'wellGroup'
          ? { WellGroupId: parseInt(formData.wellGroup), WellGroupName: wellGroup?.Name || '' }
          : {}),
    };
  };

  const handleSave = async () => {
    setTouched(Object.fromEntries(Object.keys(formData).map((k) => [k, true])));
    if (!validateForm()) return;

    try {
      setSaving(true);
      const taskData = prepareTaskData();
      const success = await saveTaskData(taskData);
      if (success) {
        showSuccessToast('Task Successfully saved');
      } else {
        showErrorToast('Task save failure', 'There was an error saving the well task. Please try again.');
      }
      if (success) {
        onTaskSaved?.();
        onClose();
      }
    } catch {
      showErrorToast('Task save failure', 'There was an error saving the well task. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return isOpen
    ? createPortal(
      <div className='modal-overlay'>
        <div className='modal-container'>
          <div className='modal-main-container'>
            <ModalHeader title={taskToEdit ? 'Edit task' : 'Add task'} onClose={onClose} />
            <main className='form-main'>
              <section className='form-section'>
                <div className='form-row'>
                  <FormField
                    label='Well'
                    name='well'
                    value={formData.well}
                    onChange={handleInputChange}
                    type='select'
                    required
                    error={errors.well}
                    options={wells.map((w) => ({ key: w.Key.toString(), value: w.Value }))}
                  />
                  <FormField
                    label='Assigned to'
                    name='assignedTo'
                    value={formData.assignedTo}
                    onChange={handleInputChange}
                    type='select'
                    required
                    error={errors.assignedTo}
                    options={users.map((u) => ({ key: u.UserId.toString(), value: u.Name }))}
                  />
                </div>
                <div className='form-row'>Please select one of the following options</div>
                <div className='form-row'>
                  <FormField
                    label='Assigned to user group'
                    name='userGroup'
                    value={formData.userGroup}
                    onChange={(n, v) => handleAssignmentTypeChange('userGroup', n, v)}
                    type='select'
                    error={errors.userGroup}
                    options={userGroups.map((g) => ({ key: g.UserGroupId.toString(), value: g.Name }))}
                  />
                  <FormField
                    label='Assigned to well group'
                    name='wellGroup'
                    value={formData.wellGroup}
                    onChange={(n, v) => handleAssignmentTypeChange('wellGroup', n, v)}
                    type='select'
                    error={errors.wellGroup}
                    options={wellGroups.map((g) => ({ key: g.WellGroupId.toString(), value: g.Name }))}
                  />
                </div>

                <div className='form-row'>
                  <FormField
                    label='Category'
                    name='category'
                    value={formData.category}
                    onChange={handleInputChange}
                    type='select'
                    options={categories.map((c) => ({
                      key: 'CategoryId' in c ? c.CategoryId.toString() : '',
                      value: 'Name' in c ? c.Name : ''
                    }))}
                  />
                  <FormField
                    label='Due date'
                    name='dueDate'
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    type='date'
                    required
                    error={errors.dueDate}
                    minDate={new Date()}
                  />
                </div>

                <FormField
                  label='Task'
                  name='task'
                  value={formData.task}
                  onChange={handleInputChange}
                  required
                  error={errors.task}
                />

                <CommentSection
                  label='Task comment'
                  value={formData.taskComment}
                  onChange={(value) => handleInputChange('taskComment', value)}
                />
              </section>
            </main>
            <footer className='form-footer'>
              <ActionButtons
                buttonText={taskToEdit ? 'Update' : 'Save'}
                onSubmit={handleSave}
                onCancel={onClose}
                isSubmitting={saving}
              />
            </footer>
          </div>
        </div>
      </div>,
      document.querySelector('.outlet-container') || document.body,
    )
    : null;
};

export default AddTaskModal;
